import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Local from 'modules/Local';
import {
  addAgency,
  addSpecialist,
  deleteAgency,
  deleteCreativeCommercial,
  deleteSpecialist,
  exportFrameSummary,
  getAgency,
  getFrameTags,
  getSpecialist,
  updateCreativeCommercial,
} from 'modules/api/content';
import { getCreativeFromState } from 'store/creative/selectors';
import { getIsReseller } from 'store/publisher/selectors';
import {
  changeProductCategory,
  removeAgency,
  removeSpecialist,
  resetCommercialId,
  setAdvertiser,
  setAgency,
  setBrand,
  setFrameTag,
  setSpecialist,
} from 'store/creative/reducer';
import { setProductCategory } from 'store/creative/actions';
import { download } from 'utils/download';
import { Creative } from 'components/common/types/Creative.types';
import { Advertiser, Agency, BrandWithProductCategory, SelectedBrand } from 'components/common/types/Deal.types';
import { FrameTag } from 'components/common/types/FrameTag.types';
import { CodeNameModel } from 'components/common/types';
import { AsyncSelectLoadDataHandler, AsyncSelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getMetaData } from 'modules/api/deal';
import { changeMeta } from 'store/dealManagement/reducer';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import {
  getAdvertiserForBrandSelection,
  getBrandForAdvertiserSelection,
} from 'components/pages/ContentManagement/utils/utils';
import { loadAdvertisersData, loadBrandsData } from 'components/pages/ContentManagement/utils/onLoadHandlers';

interface UseTaggingRulesReturn {
  creative: Creative;

  handleSelectProductCategory: AsyncSelectHandler<CodeNameModel>;
  handleExportSummary: () => Promise<void>;
  handleLoadAgencyLookupData: AsyncSelectLoadDataHandler<Agency>;
  handleLoadFrameTagsLookupData: AsyncSelectLoadDataHandler<FrameTag>;
  handleLoadSpecialistLookupData: AsyncSelectLoadDataHandler<CodeNameModel>;
  handleSelectAdvertiser: AsyncSelectHandler<Advertiser>;
  handleSelectAgency: AsyncSelectHandler<CodeNameModel>;
  handleSelectBrand: AsyncSelectHandler<SelectedBrand>;
  handleSelectFrameTag: AsyncSelectHandler<FrameTag>;
  handleSelectSpecialist: AsyncSelectHandler<CodeNameModel>;
  onLoadBrandsData: AsyncSelectLoadDataHandler<BrandWithProductCategory>;
  onLoadAdvertisersData: AsyncSelectLoadDataHandler<Advertiser>;
}

export const useTaggingRules = (
  creativeId: string,
  commercialId: string,
  onTaggingRulesChange: () => Promise<void>,
): UseTaggingRulesReturn => {
  const dispatch = useDispatch();
  const creative = useSelector(getCreativeFromState);

  const isReseller = useSelector(getIsReseller);
  const { advertiser, brand, marketId } = creative;

  const environmentId = Local.getEnvironmentId();

  const { onAssetLookup, onLoadBrands } = useLoadLookup(0);

  const onLoadBrandsData: AsyncSelectLoadDataHandler<BrandWithProductCategory> = useCallback(
    (_, query) => loadBrandsData(advertiser, onLoadBrands, _, query),
    [advertiser, onLoadBrands],
  );

  const onLoadAdvertisersData: AsyncSelectLoadDataHandler<Advertiser> = useCallback(
    (_, query) => loadAdvertisersData(brand, onAssetLookup, _, query),
    [brand, onAssetLookup],
  );

  useEffect(() => {
    const getMeta = async (): Promise<void> => {
      try {
        const metaData = await getMetaData({ cancelFunctions });
        dispatch(changeMeta(metaData));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Error fetching meta data: ${error}`);
      }
    };
    if (!isReseller) {
      getMeta();
    }
  }, [isReseller]);

  const handleLoadAgencyLookupData: AsyncSelectLoadDataHandler<Agency> = async (dropdownName, query) => {
    if (query.length < 2) {
      return [];
    }

    try {
      return await getAgency(cancelFunctions, environmentId, query);
    } catch (error) {
      return [];
    }
  };

  const handleLoadSpecialistLookupData: AsyncSelectLoadDataHandler<CodeNameModel> = async (dropdownName, query) => {
    if (query.length < 2) {
      return [];
    }

    try {
      return await getSpecialist(cancelFunctions, environmentId, query);
    } catch (error) {
      return [];
    }
  };

  const handleLoadFrameTagsLookupData: AsyncSelectLoadDataHandler<FrameTag> = async (dropdownName, query) => {
    if (query.length < 2) {
      return [];
    }

    try {
      return await getFrameTags(cancelFunctions, query, marketId);
    } catch (error) {
      return [];
    }
  };

  const handleSelectAdvertiser: AsyncSelectHandler<Advertiser> = async (_, newValue) => {
    const selectedAdvertiser = newValue || null;
    const { brands: selectedBrands } = selectedAdvertiser || [];
    const selectedBrand = getBrandForAdvertiserSelection(selectedAdvertiser, selectedBrands, brand);

    dispatch(setAdvertiser(selectedAdvertiser));

    if (commercialId && (!selectedAdvertiser || !selectedBrand)) {
      try {
        await deleteCreativeCommercial(cancelFunctions, commercialId);
        dispatch(resetCommercialId());

        if (!selectedAdvertiser) {
          dispatch(setBrand(null));
          onTaggingRulesChange();
          return;
        }
      } catch {} // eslint-disable-line no-empty
    }

    if (selectedBrand) {
      dispatch(setBrand(selectedBrand));
      dispatch(
        setProductCategory({
          cancelFunctions,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          brandCode: selectedBrand.code!,
          productCategory: selectedBrand.productCategory,
        }),
      );
    } else {
      dispatch(setBrand(null));
    }
  };

  const handleSelectBrand: AsyncSelectHandler<SelectedBrand> = async (_, newValue) => {
    const selectedBrand = newValue || null;
    const selectedAdvertiser = getAdvertiserForBrandSelection(selectedBrand, advertiser);

    if (commercialId && (!selectedBrand || !selectedAdvertiser)) {
      try {
        await deleteCreativeCommercial(cancelFunctions, commercialId);
        dispatch(resetCommercialId());

        if (!selectedBrand) {
          dispatch(setAdvertiser(null));
          onTaggingRulesChange();
          return;
        }
      } catch {} // eslint-disable-line no-empty
    }

    if (selectedBrand && selectedAdvertiser) {
      dispatch(setAdvertiser(selectedAdvertiser));
      dispatch(
        setProductCategory({
          cancelFunctions,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          brandCode: selectedBrand.code!,
          productCategory: selectedBrand.productCategory,
        }),
      );
    } else {
      dispatch(setAdvertiser(null));
    }

    if (selectedBrand) {
      dispatch(setBrand(selectedBrand));
    }

    onTaggingRulesChange();
  };

  const handleSelectAgency: AsyncSelectHandler<CodeNameModel> = async (_, newValue) => {
    const selectedAgency = newValue || { id: '', code: '', name: '' };

    try {
      if (selectedAgency.id) {
        await addAgency(cancelFunctions, marketId, selectedAgency.id);
        dispatch(setAgency(selectedAgency));
      } else {
        await deleteAgency(cancelFunctions, marketId);
        dispatch(removeAgency());
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  const handleSelectSpecialist: AsyncSelectHandler<CodeNameModel> = async (_, newValue) => {
    const selectedSpecialist = newValue || { id: '', code: '', name: '' };

    try {
      if (selectedSpecialist.id) {
        await addSpecialist(cancelFunctions, marketId, selectedSpecialist.id);
        dispatch(setSpecialist(selectedSpecialist));
      } else {
        await deleteSpecialist(cancelFunctions, marketId);
        dispatch(removeSpecialist());
      }
    } catch {} // eslint-disable-line no-empty
  };

  const handleSelectFrameTag: AsyncSelectHandler<FrameTag> = async (_, newValue) => {
    if (!newValue) return;

    dispatch(setFrameTag(newValue));
    onTaggingRulesChange();
  };

  const handleSelectProductCategory: AsyncSelectHandler<CodeNameModel> = async (_, selectedValue) => {
    const productCategory = selectedValue || null;
    if (advertiser?.code && brand?.code && commercialId && productCategory?.code) {
      try {
        await updateCreativeCommercial(
          cancelFunctions,
          commercialId,
          advertiser?.code,
          brand?.code,
          productCategory.code,
        );
      } catch {} // eslint-disable-line no-empty
    }

    dispatch(changeProductCategory(productCategory));
    onTaggingRulesChange();
  };

  const handleExportSummary = async (): Promise<void> => {
    try {
      const result = await exportFrameSummary(cancelFunctions, creativeId);

      download(new Blob([result]), 'Creative_frames_details.xlsx');
    } catch {} // eslint-disable-line no-empty
  };

  return {
    creative,
    handleSelectProductCategory,
    handleLoadAgencyLookupData,
    handleLoadFrameTagsLookupData,
    handleLoadSpecialistLookupData,
    handleSelectAdvertiser,
    handleSelectAgency,
    handleSelectBrand,
    handleSelectFrameTag,
    handleSelectSpecialist,
    handleExportSummary,
    onLoadBrandsData,
    onLoadAdvertisersData,
  };
};
